const mongoose = require('mongoose');
const Schema = mongoose.Schema;

// Definición recursiva del FieldSchema para soportar grupos y arrays de objetos
const FieldSchema = new Schema({
  key: String,
  label: String,
  abbreviation: String,  // Campo para abreviatura del label
  label2: String,
  type: String,
  required: Boolean,
  ref: String,
  isArray: Boolean,
  /**
   * Campo derivado de un grupo con `groupDisplayField`: lo genera y mantiene
   * `syncGroupDisplayFields`, no se edita a mano. Sin declararlo acá el schema (strict)
   * lo descartaba y la marca nunca llegaba a la base.
   */
  isDerivedField: Boolean,
  options: [String],
  listType: String,
  /** Marca el campo que los listados usan como flag (banderitas). */
  listFlagField: Boolean,
  clientVisibility: String, // Campo para especificar visibilidad por cliente (subdominios separados por coma)
  time: Boolean,
  accept: String,    // Para campos tipo "file"
  fileUrl: String,   // URL donde se almacenará el archivo
  fields: [this],      // Para campos tipo "group"
  itemFields: [this],  // Para campos tipo "array" de objetos
  itemType: String,    // Para arrays de tipos simples
  visualGroup: String,  // Nuevo campo para agrupación visual
  hide: { type: Boolean, default: false },  // Campo para ocultar campos en el formulario
  hidedisplay: { type: Boolean, default: false },  // Campo para ocultar campos en la visualización
  refFilter: String,    // Campo para filtrar referencias por un campo de tipo lista
  refFilterValue: String, // Valor específico del filtro de lista
  embedded: { type: Boolean, default: false },  // Campo para indicar si el formulario de referencia debe mostrarse embebido
  safe: { type: Boolean, default: false }, // Campo para indicar si el campo es seguro (requiere lápiz para editar)
  readonly: { type: Boolean, default: false }, // Campo para indicar si el campo es de solo lectura
  /** Muestra al lado del campo el botón «Ver historial» con sus cambios (sale de ModelHistory). */
  showHistory: { type: Boolean, default: false },
  addbutton: { type: Boolean, default: false }, // Campo para indicar si el campo es de solo lectura
  defaultValue: Schema.Types.Mixed, // Campo para valores por defecto (puede ser string, number, date, etc.)
  tooltip: String, // Campo para tooltips que aparecen solo en creación
  autoincrement: { type: Boolean, default: false }, // Campo para indicar si es autoincrement
  defaultGenerate: { type: Boolean, default: false }, // Nueva propiedad para generar por defecto
  preload: { type: Boolean, default: false }, // Nueva propiedad para indicar si se precarga información
  listedit: { type: Boolean, default: false }, // Nueva propiedad para indicar si el campo permite edición en listas
  /** Key of a type=reference (users) field on the same model; only that user may edit this field. */
  editableBy: { type: String, default: null },
  currency: { type: Boolean, default: false }, // Campo para indicar si es un campo de moneda
  currencyField: { type: String, default: null }, // Campo que contiene el símbolo de la moneda
  /** Moneda fija del importe (id de la lista currency), para totales que no dependen del documento:
   *  p. ej. el total de gastos en la moneda del sistema (#397). Manda sobre currencyField. */
  currencyFixed: { type: String, default: null },
  errorMessage: { type: String, default: null }, // Mensaje de error personalizado para el campo
  calendar: { type: Boolean, default: false }, // Campo para indicar si se muestra en el calendario del dashboard
  /** Ancho del campo en formularios dinámicos: 25 | 50 | 100 (% del ancho en desktop). Sin definir = layout heredado del formulario. */
  formWidth: Number,
  /** type=string: en vista se muestra como URL con enlace e icono de nueva pestaña */
  link: { type: Boolean, default: false },
  /** type=textarea: en vista el contenido se renderiza como HTML (solo contenido de confianza) */
  html: { type: Boolean, default: false },
  /**
   * type=string|text|textarea: traducciones en siblings `${key}_${locale}`
   * (p. ej. name_es, name_en). El campo base sigue siendo string.
   */
  translatable: { type: Boolean, default: false },
  /** Idiomas editables cuando `translatable` (subset de en, es, fr, uk, pt). */
  translatableLocales: { type: [String], default: [] },
  embed: { type: Boolean, default: false }, // Campo para mostrar referencias como tab embebido en vista detalle
  groupDisplayField: { type: String, default: null }, // Campo para seleccionar qué campo del grupo se mostrará (solo para group con isArray=true)
  /** Texto o clave i18n del botón que añade una fila en grupos múltiples (type=group, isArray=true). Vacío = solo «+». */
  groupAddLineLabel: { type: String, default: null },
  showInMenu: { type: Boolean, default: false }, // Para campos type=group: si es true, se muestra en el menú lateral un enlace al listado del grupo (ej. dynamic-model/shipping_expenses)
}, { _id: false });

const RuleSchema = new Schema({
  name: { type: String, required: true },
  description: { type: String },
  condition: { type: String, required: true }, // Expresión de condición
  action: { type: String, required: true },    // Expresión de acción
  targetField: { type: String, required: true }, // Campo que se actualizará
  priority: { type: Number, default: 0 },      // Orden de ejecución
  isActive: { type: Boolean, default: true },
  validationMessage: { type: String }          // Mensaje de validación
}, { _id: true });

const VisualRuleSchema = new Schema({
  targetField: { type: String, required: true }, // Campo que se controla
  condition: { type: String, required: true },   // Condición (equals, notEquals, etc.)
  value: { type: String },                       // Valor a comparar
  action: { type: String, required: true },      // Acción (show/hide/require: obligatorio si se cumple)
  fieldsToToggle: [{ type: String }]            // Campos a mostrar/ocultar/hacer obligatorios
}, { _id: false });

/** Opciones visibles en un listado destino según el ítem elegido en otro listado (FormV2). */
const ListOptionMappingSchema = new Schema({
  driverFieldKey: { type: String, required: true },
  driverItemId: { type: String, required: true },
  targetFieldKey: { type: String, required: true },
  allowedTargetItemIds: [{ type: String }],
}, { _id: false });

/** Prompts por acción (IA / listados / fichas dinámicas). */
const AIActionPromptsSchema = new Schema({
  editItem: { type: String, default: '', trim: true },
  addItem: { type: String, default: '', trim: true },
  deleteItem: { type: String, default: '', trim: true },
  searchItem: { type: String, default: '', trim: true },
}, { _id: false });

/** Reglas por campo (clave técnica, p. ej. grupo.subcampo). */
const AIFieldPromptRowSchema = new Schema({
  fieldKey: { type: String, required: true, trim: true },
  prompt: { type: String, default: '', trim: true },
}, { _id: false });

const ModelDefinitionSchema = new Schema({  
  modelName: { type: String, required: true, unique: true },
  label: { type: String, required: false },
  description: { type: String, required: false, default: '' },
  icon: { type: String, required: false },  // Nuevo campo para el icono del modelo
  order: { type: Number, default: 0 },      // Campo para ordenar los modelos
  hideFromMenu: { type: Boolean, default: false },  // Campo para ocultar el modelo del menú lateral
  /** Modelo de sistema (p. ej. User): no se puede eliminar desde model-definitions */
  isSystemModel: { type: Boolean, default: false },
  autoSave: { type: Boolean, default: true },       // Activar auto-guardado en formularios de este modelo
  /** Permite archivar registros desde el listado (ocultar sin borrar). */
  archiveEnabled: { type: Boolean, default: false },
  /** Campo user (o createdBy/updatedBy) usado para filtrar "mis registros" en listados. */
  userOwnerField: { type: String, default: null, trim: true },
  /** Varios campos user: "mis registros" matchea si el usuario aparece en cualquiera. */
  userOwnerFields: { type: [String], default: () => [] },
  fields: [FieldSchema],
  name: {
    type: String,
    required: true
  },
  visualGroups: [{     // Nuevo campo para definir grupos visuales
    name: String,
    label: String,
    icon: String,
    type: { type: String, enum: ['fieldset', 'box'], default: 'fieldset' },
    description: String,
    order: { type: Number, default: 1 },  // Campo para ordenar los grupos visuales
    roleRestrictions: [{  // Restricciones de visibilidad por rol
      roleName: String,  // Nombre del rol
      canView: { type: Boolean, default: true },  // Puede ver el grupo
      canEdit: { type: Boolean, default: true }  // Puede editar campos del grupo
    }]
  }],
  rules: [RuleSchema],  // Nuevo campo para las reglas
  visualRules: [VisualRuleSchema],  // Nuevo campo para las reglas visuales
  listOptionMappings: [ListOptionMappingSchema],
  // Nuevo campo para contexto específico de IA
  aiContext: {
    type: String,
    default: '',
    trim: true
  },
  aiActionPrompts: {
    type: AIActionPromptsSchema,
    default: () => ({}),
  },
  aiFieldPrompts: {
    type: [AIFieldPromptRowSchema],
    default: () => [],
  },
}, {
  timestamps: true
});

module.exports = mongoose.model('ModelDefinition', ModelDefinitionSchema); 